/**
 * src/v2/services/defindexService.ts
 *
 * Client-side service for querying DeFindex vault balances via the GeoTrust
 * backend proxy (which simulates read-only contract calls without requiring
 * an on-chain transaction or wallet connection).
 */

const API_BASE = '/api/v2/defindex';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AssetAllocation {
  asset?: string;
  total_amount?: string;
  idle_amount?: string;
  invested_amount?: string;
}

export interface DefindexVaultBalance {
  success: boolean;
  vaultAddress: string;
  vaultName: string;
  userAddress: string;
  /** Raw share token balance (i128 as string) */
  shareBalance: string;
  /** Total shares in circulation (i128 as string) */
  totalSupply: string;
  /** Total XLM-equivalent managed across all strategies (stroops as string) */
  totalManagedFundsStroops: string;
  /** User's proportional underlying value in stroops */
  underlyingStroops: string;
  /** Human-friendly XLM amount with 7 decimals */
  underlyingXlm: string;
  /** Human-friendly share balance with 7 decimals */
  shareBalanceDisplay: string;
  /** Per-asset breakdown */
  allocations: AssetAllocation[];
  error?: string;
  /** Wallet-only (G-address) portion — set when passkey account is also present */
  walletUnderlyingXlm?: string;
  /** Passkey smart-account portion */
  passkeyUnderlyingXlm?: string;
  walletShareBalance?: string;
  passkeyShareBalance?: string;
}

export interface KnownVault {
  address: string;
  name: string;
}

// ── API calls ─────────────────────────────────────────────────────────────────

/**
 * Get a user's position in a specific DeFindex vault.
 * Returns share balance, total supply, and calculated underlying XLM value.
 */
export async function getDefindexVaultBalance(
  vaultAddress: string,
  userAddress: string,
): Promise<DefindexVaultBalance> {
  const url = new URL(`${API_BASE}/balance`, window.location.origin);
  url.searchParams.set('vaultAddress', vaultAddress);
  url.searchParams.set('userAddress', userAddress);
  const resp = await fetch(url.toString());
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error((err as any).error || `HTTP ${resp.status}`);
  }
  return resp.json();
}

/**
 * Get the list of DeFindex vaults tracked by this GeoTrust instance.
 */
export async function getKnownDefindexVaults(): Promise<KnownVault[]> {
  const resp = await fetch(`${API_BASE}/registry`);
  if (!resp.ok) return FALLBACK_KNOWN_VAULTS;
  const data = await resp.json();
  return (data.vaults as KnownVault[]) ?? FALLBACK_KNOWN_VAULTS;
}

/**
 * Fetch balances for ALL known vaults for a given user in one parallel call.
 */
export async function getAllDefindexBalances(
  userAddress: string,
): Promise<DefindexVaultBalance[]> {
  const vaults = await getKnownDefindexVaults();
  const results = await Promise.allSettled(
    vaults.map(v => getDefindexVaultBalance(v.address, userAddress)),
  );
  return results
    .map((r, i) =>
      r.status === 'fulfilled'
        ? r.value
        : {
            success: false,
            vaultAddress: vaults[i].address,
            vaultName: vaults[i].name,
            userAddress,
            shareBalance: '0',
            totalSupply: '0',
            totalManagedFundsStroops: '0',
            underlyingStroops: '0',
            underlyingXlm: '0.0000000',
            shareBalanceDisplay: '0.0000000',
            allocations: [],
            error: (r.reason as Error)?.message,
          },
    );
}

// Shown when the registry endpoint is unavailable
const FALLBACK_KNOWN_VAULTS: KnownVault[] = [
  { address: 'CCLV4H7WTLJQ7ATLHBBQV2WW3OINF3FOY5XZ7VPHZO7NH3D2ZS4GFSF6', name: 'paltalabs XLM' },
];
